// client/src/components/ClientScanQRC.js

import React, { useEffect, useState, useCallback, useRef, useContext } from 'react';
import { AuthContext } from '../../AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import '../CSS/Dashboard.css';
import { firestore, storage, auth } from '../../firebase';
import {
  doc,
  updateDoc,
  getDoc,
  setDoc,
  serverTimestamp,
  deleteDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  limit
} from 'firebase/firestore';
import { ref, list, getDownloadURL, getMetadata } from 'firebase/storage';
import {
  onAuthStateChanged,
  EmailAuthProvider,
  linkWithCredential,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  sendEmailVerification,
} from 'firebase/auth';
import { FaTimesCircle, FaUser, FaCheckCircle } from 'react-icons/fa';
import { ensureUserSession } from '../../utils/ensureUserSession';
import LegalAccordion from '../shared/LegalAccordion';
import { sanitize } from '../../utils/sanitize';
import Barcode from 'react-barcode';
import { REGEX as PASSWORD_REGEX, POLICY as PASSWORD_POLICY } from '../../policies/passwordPolicy';
import PasswordField from '../shared/PasswordField';

/* =========================
   Caches & TTLs
========================= */
const LOGO_TTL_MS = 7 * 24 * 60 * 60 * 1000;        // 7 jours
const MERCHANT_PROFILE_TTL_MS = 24 * 60 * 60 * 1000; // 24 h
const MERCHANT_INDEX_TTL_MS = 6 * 60 * 60 * 1000;    // 6 h : profil/logo peu volatiles
const scanIndexKey = (clientUid) => `scan_index_${clientUid}`;
const nowMs = () => Date.now();

const logoKey = (uid) => `merchantLogo_${uid}`;         // { url, ts }
const profileKey = (uid) => `merchantProfile_${uid}`;   // { pseudo, email, ts }
const ratingFlagKey = (merchantUid) => `pendingRating_${merchantUid}`;

// Extraction robuste du pseudo marchand (plusieurs schémas possibles)
const extractPseudo = (data = {}) => (
  data.pseudo ||
  data.companyPseudo ||
  data.entreprise_name ||
  data.companyName ||
  data.company_name ||
  data.name ||
  data.brand ||
  data.alias ||
  data.displayName ||
  data.enseigne ||
  null
);

const fromCache = (key, ttl) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== 'object') return null;
    if (typeof data.ts !== 'number') return null;
    if (Date.now() - data.ts > ttl) return null;
    return data;
  } catch {
    return null;
  }
};
const putCache = (key, payload) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ...payload, ts: Date.now() }));
  } catch {}
};

// ----- Ajoute un marchand au cache fid_merchants_{clientUid} (utilisé par ClientFid/ClientDocs) -----
const addMerchantToLocalCache = (merchantId, clientUid) => {
  if (!merchantId || !clientUid) return;
  try {
    const key = `fid_merchants_${clientUid}`;
    const raw = localStorage.getItem(key);
    const existing = raw ? JSON.parse(raw) : [];
    const updated = Array.from(new Set([...(Array.isArray(existing) ? existing : []), merchantId])).sort();
    localStorage.setItem(key, JSON.stringify(updated));
  } catch {}
};

// ----- Index helpers (per client → map merchant => {pseudo, profilePhotoUrl, ts}) -----
const loadIndexCache = (clientUid) => {
  if (!clientUid) return null;
  try {
    const raw = localStorage.getItem(scanIndexKey(clientUid));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const ts = parsed?.ts || 0;
    if (!ts || nowMs() - ts > MERCHANT_INDEX_TTL_MS) return null;
    return parsed.payload || null;
  } catch {
    return null;
  }
};

const saveIndexCache = (clientUid, payload) => {
  if (!clientUid || !payload) return;
  try {
    localStorage.setItem(scanIndexKey(clientUid), JSON.stringify({ ts: nowMs(), payload }));
  } catch {}
};

const readIndexEntry = (payload, merchantUid) => {
  if (!payload || !merchantUid) return null;
  const entry = payload.merchants?.[merchantUid];
  if (!entry || typeof entry.ts !== 'number') return null;
  if (nowMs() - entry.ts > MERCHANT_INDEX_TTL_MS) return null;
  return entry;
};

const normalizeEntry = (entry = {}) => ({
  pseudo: entry.pseudo || 'Inconnu',
  profilePhotoUrl: entry.profilePhotoUrl ?? null,
  ts: entry.ts || nowMs(),
});

const isMeaningfulPseudo = (p) => p && p !== 'Inconnu';

// Plus d'index distant : on ne lit rien depuis Firestore
const loadRemoteIndex = async () => null;

const persistIndexEntry = async (clientUid, merchantUid, partialEntry, merge = true, sigRef, tsRef) => {
  if (!clientUid || !merchantUid) return;
  const normalized = normalizeEntry(partialEntry);
  const payload = { merchants: { [merchantUid]: normalized }, updatedAt: nowMs() };
  const sig = JSON.stringify(payload);
  if (sigRef && sigRef.current === sig) return;
  if (sigRef) sigRef.current = sig;

  // Local cache merge
  const cached = loadIndexCache(clientUid) || { merchants: {} };
  const merged = {
    merchants: {
      ...(merge ? cached.merchants || {} : {}),
      [merchantUid]: normalized,
    },
    updatedAt: payload.updatedAt,
  };
  saveIndexCache(clientUid, merged);
  if (tsRef) tsRef.current = payload.updatedAt;
};

/* =========================
   Component
========================= */
const ClientScanQRC = () => {
  const { uid } = useParams(); // uid = UID du commerçant
  const navigate = useNavigate();

  const [generatedCode, setGeneratedCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [validationFailure, setValidationFailure] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const [merchantPhotoURL, setMerchantPhotoURL] = useState(null);
  const [merchantPseudo, setMerchantPseudo] = useState('');

  const { reloadEmailVerified } = useContext(AuthContext);

  // CGV
  const [showFlowModal, setShowFlowModal] = useState(false);
  const [activeStep, setActiveStep] = useState(1); // 1 = CGV, 2 = création compte (optionnelle)
  const [acceptedCGV, setAcceptedCGV] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPseudo, setNewPseudo] = useState('');
  const [isPwdValid, setIsPwdValid] = useState(true);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginHint, setLoginHint] = useState(null);

  // Locks anti-doublons réseau
  const genCodeBusyRef = useRef(false);
  const mountedRef = useRef(true);
  const preservedCodeRef = useRef(null); // pour réutiliser un code après login
  const autoClosedRef = useRef(false);
  const successTimeoutRef = useRef(null);
  const indexSigRef = useRef('');
  const indexTsRef = useRef(0);

  // Hydrate depuis index (local puis remote) pour pseudo/logo avant tout réseau lourd
  const hydrateMerchantFromIndex = useCallback(async () => {
    const clientUid = auth.currentUser?.uid;
    if (!clientUid || !uid) return false;

    const cached = loadIndexCache(clientUid);
    const cachedEntry = readIndexEntry(cached, uid);
    if (cachedEntry) {
      if (cachedEntry.profilePhotoUrl) setMerchantPhotoURL((prev) => prev || cachedEntry.profilePhotoUrl);
      if (cachedEntry.pseudo) setMerchantPseudo((prev) => prev || cachedEntry.pseudo);
      indexSigRef.current = JSON.stringify(cached);
      indexTsRef.current = cached.updatedAt || cachedEntry.ts || nowMs();
      return true;
    }

    const remote = await loadRemoteIndex(clientUid);
    const remoteEntry = readIndexEntry(remote, uid);
    if (remote) {
      saveIndexCache(clientUid, remote);
      indexSigRef.current = JSON.stringify(remote);
      indexTsRef.current = remote.updatedAt || remoteEntry?.ts || nowMs();
    }
    if (remoteEntry) {
      if (remoteEntry.profilePhotoUrl) setMerchantPhotoURL((prev) => prev || remoteEntry.profilePhotoUrl);
      if (remoteEntry.pseudo) setMerchantPseudo((prev) => prev || remoteEntry.pseudo);
      return true;
    }
    return false;
  }, [uid]);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = null;
      }
    };
  }, []);

  /* =========================
     1) Merchant logo : cache + refresh silencieux
     - list() (≤ 20 items) + getMetadata pour trier par timeCreated
     - on ne relance pas si cache valide
  ========================= */
  const fetchMerchantLogo = useCallback(async () => {
    if (!uid) return;

    const k = logoKey(uid);
    const cached = fromCache(k, LOGO_TTL_MS);
    if (cached?.url !== undefined) setMerchantPhotoURL(cached.url);

    // Index client → merchant
    const clientUid = auth.currentUser?.uid;
    const idx = loadIndexCache(clientUid);
    const idxEntry = readIndexEntry(idx, uid);
    if (idxEntry?.profilePhotoUrl) {
      setMerchantPhotoURL((prev) => prev || idxEntry.profilePhotoUrl);
      if (idxEntry && nowMs() - idxEntry.ts <= LOGO_TTL_MS) return;
    }

    if (cached) return; // cache frais → rien à faire

    try {
      const profilePhotoRef = ref(storage, `${uid}/photo-profil`);
      // une seule page, bornée (coût faible)
      const first = await list(profilePhotoRef, { maxResults: 20 });
      if (first.items.length === 0) {
        setMerchantPhotoURL(null);
        putCache(k, { url: null });
        return;
      }
      const withMeta = await Promise.all(
        first.items.map(async (item) => {
          const meta = await getMetadata(item);
          return { item, t: meta.timeCreated ? new Date(meta.timeCreated).getTime() : 0 };
        })
      );
      withMeta.sort((a, b) => b.t - a.t);
      const url = await getDownloadURL(withMeta[0].item);
      if (!mountedRef.current) return;
      setMerchantPhotoURL(url);
      putCache(k, { url });
      const clientUid = auth.currentUser?.uid;
      persistIndexEntry(clientUid, uid, { profilePhotoUrl: url }, /*merge*/ true, indexSigRef, indexTsRef);
    } catch {
      // silencieux
    }
  }, [uid]);

  /* =========================
     2) Merchant profile : cache + refresh silencieux
  ========================= */
  const fetchMerchantProfile = useCallback(async () => {
    if (!uid) return;

    const k = profileKey(uid);
    const cached = fromCache(k, MERCHANT_PROFILE_TTL_MS);
    if (cached?.pseudo) setMerchantPseudo(cached.pseudo);

    // Index client → merchant (évite getDoc si déjà frais)
    const clientUid = auth.currentUser?.uid;
    const idx = loadIndexCache(clientUid);
    const idxEntry = readIndexEntry(idx, uid);
    if (idxEntry?.pseudo) {
      const meaningfulIdxPseudo = isMeaningfulPseudo(idxEntry.pseudo);
      setMerchantPseudo((prev) => prev || idxEntry.pseudo);
      // si index récent et cache déjà alimenté, pas besoin de relire Firestore
      if (idxEntry.profilePhotoUrl && !merchantPhotoURL) setMerchantPhotoURL(idxEntry.profilePhotoUrl);
      if (meaningfulIdxPseudo && idxEntry && nowMs() - idxEntry.ts <= MERCHANT_PROFILE_TTL_MS) return;
    }

    try {
      const entSnap = await getDoc(doc(firestore, 'entreprises', uid));
      let pseudo = null;
      if (entSnap.exists()) {
        const d = entSnap.data() || {};
        pseudo = extractPseudo(d);
      }

      if (!pseudo) {
        // fallback: doc id != owner uid -> query by owner
        const q = query(collection(firestore, 'entreprises'), where('userId', '==', uid), limit(1));
        const qs = await getDocs(q);
        if (!qs.empty) {
          const d = qs.docs[0].data() || {};
          pseudo = extractPseudo(d);
        }
      }

      if (!mountedRef.current) return;
      if (pseudo) {
        setMerchantPseudo(pseudo);
        putCache(k, { pseudo });
        persistIndexEntry(clientUid, uid, { pseudo, profilePhotoUrl: merchantPhotoURL || null }, /*merge*/ true, indexSigRef, indexTsRef);
        return;
      }
    } catch {
      // ignore entreprise fetch errors
    }

    try {
      const snap = await getDoc(doc(firestore, 'users', uid));
      const pseudo = snap.exists() ? (extractPseudo(snap.data()) || 'Inconnu') : 'Inconnu';
      if (!mountedRef.current) return;
      setMerchantPseudo(pseudo);
      putCache(k, { pseudo });
      persistIndexEntry(clientUid, uid, { pseudo, profilePhotoUrl: merchantPhotoURL || null }, /*merge*/ true, indexSigRef, indexTsRef);
    } catch {
      // fallback silencieux
    }

    // Dernier filet: s'assurer qu'on n'affiche pas une chaîne vide
    setMerchantPseudo((prev) => prev || 'Inconnu');
  }, [uid, merchantPhotoURL]);

  /* =========================
     3) Génération/écriture code (1 write)
     - verrou in-flight pour éviter doubles clics
     - lecture pseudo client best-effort
  ========================= */
  const generateCodeFromUid = useCallback(async () => {
    if (genCodeBusyRef.current) return;
    genCodeBusyRef.current = true;
    setQrLoading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return;

      // pseudo client best-effort (0 read si déjà présent en d’autres écrans)
      let userPseudo = 'Inconnu';
      try {
        const userSnap = await getDoc(doc(firestore, 'users', currentUser.uid));
        if (userSnap.exists()) userPseudo = userSnap.data()?.pseudo || 'Inconnu';
      } catch {}

      // Réutilisation d’un code préservé après login si disponible
      const reuseCode = preservedCodeRef.current;
      if (reuseCode) {
        preservedCodeRef.current = null;
        try {
          const listDocRef = doc(firestore, 'fidelity', uid, 'list', currentUser.uid);
          await setDoc(
            listDocRef,
            {
              uid: currentUser.uid,
              clientUid: currentUser.uid,
              pseudo: userPseudo,
              code: reuseCode,
              createdAt: serverTimestamp()
            },
            { merge: true }
          );
          addMerchantToLocalCache(uid, currentUser.uid);
          if (mountedRef.current) setGeneratedCode(reuseCode);
          return;
        } catch (err) {
          console.warn('Réutilisation code préservé échouée, on regénère', err);
        }
      }

      const listDocRef = doc(firestore, 'fidelity', uid, 'list', currentUser.uid);
      const listDocSnap = await getDoc(listDocRef);

      // Code sauvegardé avant la redirection email (localStorage, partagé entre onglets, TTL 30 min)
      const CODE_BACKUP_TTL = 30 * 60 * 1000;
      const savedCode = (() => {
        try {
          const raw = localStorage.getItem(`ecobill_code_${uid}`);
          if (!raw) return null;
          const { code, ts } = JSON.parse(raw);
          if (!code || Date.now() - ts > CODE_BACKUP_TTL) { localStorage.removeItem(`ecobill_code_${uid}`); return null; }
          return code;
        } catch { return null; }
      })();
      if (savedCode) {
        try { localStorage.removeItem(`ecobill_code_${uid}`); } catch {}
        if (listDocSnap.exists() && listDocSnap.data()?.code) {
          // Scénario 1 : code encore présent → le réutiliser tel quel
          if (mountedRef.current) setGeneratedCode(listDocSnap.data().code);
          return;
        }
        // Scénario 2 : document absent ou code effacé → marchand a déjà crédité les points
        if (mountedRef.current) navigate(`/clientfidviewer/${uid}`);
        return;
      }

      // Si un code existe déjà (re-mount normal) → le réutiliser
      if (listDocSnap.exists() && listDocSnap.data()?.code) {
        if (mountedRef.current) setGeneratedCode(listDocSnap.data().code);
        return;
      }

      // Nouveau code (première visite ou code consommé hors flow email)
      const newCode = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
      await setDoc(listDocRef, {
        uid: currentUser.uid,
        clientUid: currentUser.uid,
        pseudo: userPseudo,
        code: newCode,
        createdAt: serverTimestamp()
      }, { merge: true });
      addMerchantToLocalCache(uid, currentUser.uid);

      if (!mountedRef.current) return;
      setGeneratedCode(newCode);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erreur generateCodeFromUid :', error);
      if (mountedRef.current) {
        setValidationFailure(true);
        setTimeout(() => setValidationFailure(false), 1500);
      }
    } finally {
      if (mountedRef.current) setQrLoading(false);
      genCodeBusyRef.current = false;
    }
  }, [uid, navigate]);

  /* =========================
     4) Fermer et basculer vers le suivi fidélité
  ========================= */
  const handleClose = async () => {
    if (!uid) return;
    try {
      localStorage.setItem(ratingFlagKey(uid), JSON.stringify({ ts: Date.now() }));
    } catch {}
    autoClosedRef.current = true;
    navigate(`/clientfidviewer/${uid}`);
  };

  /* =========================
     Création compte pour anonymes
  ========================= */
  const handlePwdChange = (e) => {
    const v = e.target.value;
    setNewPassword(v);
    setIsPwdValid(PASSWORD_REGEX.test(v));
  };

  const handleCreateAccount = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !currentUser.isAnonymous) return;

    const mail = sanitize(newEmail.trim());
    const pwd = sanitize(newPassword.trim());
    const pseudo = sanitize(newPseudo.trim());

    if (!mail || !pwd || !pseudo) {
      alert('Veuillez remplir tous les champs.');
      return;
    }
    if (!PASSWORD_REGEX.test(pwd)) {
      alert(PASSWORD_POLICY.message);
      return;
    }
    if (!acceptedCGV) {
      alert('Veuillez accepter les CGV.');
      return;
    }

    const credential = EmailAuthProvider.credential(mail, pwd);
    try {
      const linkedUser = await linkWithCredential(currentUser, credential);
      const userRef = doc(firestore, 'users', linkedUser.user.uid);
      await setDoc(
        userRef,
        { email: mail, pseudo, createdAt: serverTimestamp(), role: 'client', CGV: 'accepted' },
        { merge: true }
      );

      // Poser le claim needsEmailVerification + envoyer le mail de vérification
      try {
        const idToken = await linkedUser.user.getIdToken();
        const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '') ||
          (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:5099');
        await fetch(`${apiBase}/api/auth/flag-verification`, {
          method: 'POST',
          credentials: 'include',
          headers: { Authorization: `Bearer ${idToken}` },
        });
        await sendEmailVerification(linkedUser.user, { url: `${window.location.origin}/clientfidviewer/${uid}` });
        // Sauvegarder le code en localStorage (TTL 30 min) avant que la gate email ne cache ClientScanQRC.
        // localStorage est partagé entre onglets (le client mail ouvre souvent le lien dans un nouvel onglet).
        // Si generatedCode est null (race condition ou échec silencieux de generateCodeFromUid),
        // on relit le code directement depuis Firestore comme fallback.
        try {
          let codeToSave = generatedCode;
          if (!codeToSave) {
            const snap = await getDoc(doc(firestore, 'fidelity', uid, 'list', linkedUser.user.uid));
            codeToSave = snap.data()?.code || null;
          }
          if (codeToSave) {
            localStorage.setItem(`ecobill_code_${uid}`, JSON.stringify({ code: codeToSave, ts: Date.now() }));
          }
        } catch {}
        // Force la réévaluation du gate avec refresh du token (claim maintenant posé côté backend)
        await reloadEmailVerified(true);
      } catch (e) {
        console.warn('email verification setup error:', e);
      }

      setShowFlowModal(false);
      setAcceptedCGV(true);
    } catch (error) {
      console.error('Erreur lors de la création du compte', error);
      if (error.code === 'auth/email-already-in-use') {
        setLoginEmail(mail);
        setLoginHint('Cet email est déjà associé à un compte. Connectez-vous.');
        setShowLoginForm(true);
      } else {
        alert('Erreur lors de la création du compte : ' + error.message);
      }
    }
  };

  const handleLoginExisting = async () => {
    const email = sanitize(loginEmail.trim());
    const pwd = sanitize(loginPassword.trim());
    if (!email || !pwd) {
      alert('Veuillez saisir votre email et mot de passe.');
      return;
    }
    if (!acceptedCGV) {
      alert('Veuillez accepter les CGV avant de continuer.');
      return;
    }
    const previousCode = generatedCode;
    setIsLoginLoading(true);
    try {
      const currentUser = auth.currentUser;
      if (currentUser?.uid && uid) {
        try {
          const listDocRef = doc(firestore, 'fidelity', uid, 'list', currentUser.uid);
          await deleteDoc(listDocRef);
        } catch (err) {
          console.warn('Delete code avant login existing failed:', err);
        }
      }

      // On quitte la session anonyme pour se connecter au compte existant
      setGeneratedCode(null);
      preservedCodeRef.current = previousCode || null;
      // IMPORTANT : ne pas signOut ici. signOut met userRole à null, ProtectedRoute
      // redirige vers la landing et la modale est démontée. Firebase remplace
      // automatiquement la session anonyme quand on se connecte avec un compte existant.
      const cred = await signInWithEmailAndPassword(auth, email, pwd);
      await updateDoc(doc(firestore, 'users', cred.user.uid), { CGV: 'accepted' });
      if (previousCode && uid) {
        try {
          const userSnap = await getDoc(doc(firestore, 'users', cred.user.uid));
          const pseudo = userSnap.exists() ? (userSnap.data()?.pseudo || 'Inconnu') : (cred.user.email || 'Inconnu');
          const listDocRef = doc(firestore, 'fidelity', uid, 'list', cred.user.uid);
          await setDoc(
            listDocRef,
            {
              uid: cred.user.uid,
              clientUid: cred.user.uid,
              pseudo,
              code: previousCode,
              createdAt: serverTimestamp()
            },
            { merge: true }
          );
          addMerchantToLocalCache(uid, cred.user.uid);
          setGeneratedCode(previousCode);
        } catch (err) {
          console.warn('Réutilisation du code après login échouée:', err);
        }
      }
      setAcceptedCGV(true);
      setShowFlowModal(false);
      setShowLoginForm(false);
      setActiveStep(1);
      const target = uid ? encodeURIComponent(uid) : '';
      navigate(`/clientscanqrc/${target}`, { replace: true });
    } catch (error) {
      console.error('Erreur connexion compte existant', error);
      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        alert('Identifiants invalides. Vérifiez votre email et mot de passe.');
      } else if (error.code === 'auth/user-not-found') {
        alert("Aucun compte trouvé avec cet email.");
      } else {
        alert('Erreur : ' + error.message);
      }
    } finally {
      setIsLoginLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const raw = sanitize(loginEmail.trim());
    if (!raw) {
      alert('Renseignez votre email pour réinitialiser le mot de passe.');
      return;
    }
    // Normalisation simple (comme LoginGeneral) pour éviter les erreurs user-not-found dues au domaine
    const at = raw.lastIndexOf('@');
    const local = at === -1 ? raw : raw.slice(0, at);
    const domain = at === -1 ? '' : raw.slice(at + 1).toLowerCase();
    const normalizedDomain = domain === 'googlemail.com' ? 'gmail.com' : domain;
    const target = at === -1 ? raw : `${local}@${normalizedDomain}`;

    try {
      await sendPasswordResetEmail(auth, target);
      alert('Si un compte existe pour cet email, un lien a été envoyé.');
    } catch (error) {
      // on ne bloque pas l’UX : message neutre, même en cas d'user-not-found
      console.error('Erreur reset password', error);
      alert('Si un compte existe pour cet email, un lien a été envoyé.');
    }
  };

  /* =========================
     5) CGV
  ========================= */
  const handleSaveCGV = async () => {
    if (!acceptedCGV) {
      alert('Veuillez accepter les CGV.');
      return;
    }
    const cu = auth.currentUser;
    if (!cu) return;
    try {
      await updateDoc(doc(firestore, 'users', cu.uid), { CGV: 'accepted' });
      setAcceptedCGV(true);
      if (cu.isAnonymous) {
        setShowLoginForm(false);
        setActiveStep(2);
        setShowFlowModal(true);
      } else {
        setShowLoginForm(false);
        setShowFlowModal(false);
      }
    } catch {
      // ignore
    }
  };

  /* =========================
     6) Flux principal (SWR)
     - sert les caches immédiatement
     - refresh silencieux
     - génération code
     - check CGV
  ========================= */
  useEffect(() => {
    mountedRef.current = true;
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      if (!uid) {
        navigate('/clientcamera');
        return;
      }

      try {
        // Session (anonyme si besoin)
        // ignoreLastSeenUid: false pour ne pas créer un nouveau compte si l'utilisateur
        // avait déjà une session (même anonyme) — la restauration passe par RedirectQRC.
        const user = await ensureUserSession({
          allowNewAnonymous: true,
          overrideLogoutGuard: true,
          ignoreLastSeenUid: false
        });
        if (cancelled) return;

        setAuthChecked(true);

        // Servir les caches (sans attendre le réseau)
        const prof = fromCache(profileKey(uid), MERCHANT_PROFILE_TTL_MS);
        if (prof?.pseudo) setMerchantPseudo(prof.pseudo);

        const logo = fromCache(logoKey(uid), LOGO_TTL_MS);
        if (logo?.url !== undefined) setMerchantPhotoURL(logo.url);

        // Index (local/remote) pour éviter scans Storage/users si déjà frais
        await hydrateMerchantFromIndex();

        // Refresh silencieux + génération du code
        await Promise.all([fetchMerchantProfile(), fetchMerchantLogo(), generateCodeFromUid()]);
        if (cancelled) return;

        const isAnon = !user || user.isAnonymous || !user.email;
        let hasAccepted = false;
        try {
          const userRef = doc(firestore, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          hasAccepted = userSnap.exists() && userSnap.data()?.CGV === 'accepted';
          if (!cancelled) {
            const pseudoFromDoc = userSnap.exists() ? (userSnap.data()?.pseudo || '') : '';
            if (pseudoFromDoc) setNewPseudo(pseudoFromDoc);
          }
        } catch {
          // ignore
        }

        if (!cancelled) {
          setAcceptedCGV(hasAccepted);
          if (isAnon) {
            setShowLoginForm(false);
            setActiveStep(hasAccepted ? 2 : 1);
            setShowFlowModal(true);
          } else {
            setShowLoginForm(false);
            setActiveStep(1);
            setShowFlowModal(!hasAccepted);
          }
        }

        // Keep-alive auth
        unsubscribe = onAuthStateChanged(auth, (u) => {
          if (!u) {
            ensureUserSession({
              allowNewAnonymous: true,
              overrideLogoutGuard: true,
              ignoreLastSeenUid: false
            }).catch(() => {});
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[ClientScanQRC] init error:', error);
        setAuthChecked(true);
      }
    })();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      unsubscribe();
    };
  }, [uid, navigate, fetchMerchantProfile, fetchMerchantLogo, generateCodeFromUid, hydrateMerchantFromIndex]);

  // Ferme automatiquement l'écran dès que le commerçant consomme le code
  useEffect(() => {
    if (!uid || !generatedCode) return undefined;
    const currentUser = auth.currentUser;
    if (!currentUser?.uid) return undefined;

    const targetRef = doc(firestore, 'fidelity', uid, 'list', currentUser.uid);
    const unsubscribe = onSnapshot(
      targetRef,
      (snap) => {
        const code = snap.exists() ? snap.data()?.code : null;
        if (!code && !autoClosedRef.current && !showFlowModal) {
          try {
            localStorage.setItem(ratingFlagKey(uid), JSON.stringify({ ts: Date.now() }));
          } catch {}
          autoClosedRef.current = true;
          setShowSuccessAnimation(true);
          successTimeoutRef.current = setTimeout(() => {
            navigate(`/clientfidviewer/${uid}`, { replace: true });
            successTimeoutRef.current = null;
          }, 2200);
        }
      },
      (err) => {
        console.error('Auto-close listener error:', err);
      }
    );
    return () => unsubscribe();
  }, [uid, generatedCode, navigate, showFlowModal]);

  const isCreateDisabled = !newEmail.trim() || !newPassword.trim() || !newPseudo.trim() || !isPwdValid || !acceptedCGV;

  /* =========================
     Render
  ========================= */
  if (!authChecked) {
    return (
      <div className="eb-screen">
        <div className="eb-screen__panel">
          <div className="eb-spinner" aria-hidden="true" />
          <p className="eb-screen__text">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="csq-page">
      <style>{CSQ_STYLES}</style>

      {/* Modale unique avec frise 2 étapes */}
      {showFlowModal && (
        <div className="csq-overlay">
          <div className="csq-modal eb-fade-up" role="dialog" aria-modal="true">
            {activeStep === 2 && (
              <button
                type="button"
                onClick={() => setShowFlowModal(false)}
                className="csq-modal__close"
                aria-label="Fermer"
              >
                &times;
              </button>
            )}

            {/* Frise simple */}
            <div className="csq-steps" aria-label={`Étape ${activeStep} sur 2`}>
              {[1, 2].map((step, idx) => {
                const isActive = activeStep === step;
                const isDone = activeStep > step;
                return (
                  <React.Fragment key={step}>
                    <div className={`csq-step${isActive ? ' is-active' : ''}${isDone ? ' is-done' : ''}`}>
                      {step}
                    </div>
                    {idx === 0 && (
                      <div className="csq-steps__track">
                        <div
                          className="csq-steps__fill"
                          style={{ width: isDone ? '100%' : isActive ? '50%' : '0%' }}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {activeStep === 1 && (
              <div className="csq-modal__body">
                <LegalAccordion
                  docId="cgu"
                  checked={acceptedCGV}
                  onChange={setAcceptedCGV}
                />
                <button
                  type="button"
                  onClick={handleSaveCGV}
                  className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
                  disabled={!acceptedCGV}
                >
                  Continuer
                </button>
              </div>
            )}

            {activeStep === 2 && (
              <>
                {!showLoginForm && (
                  <div className="csq-modal__body">
                    <h2 className="csq-modal__title">Créer mon compte</h2>

                    <input
                      type="email"
                      className="eb-input"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      placeholder="Email"
                      autoComplete="email"
                      inputMode="email"
                      aria-label="Email"
                    />

                    <div className="csq-pwd">
                      <PasswordField
                        value={newPassword}
                        onChange={handlePwdChange}
                        placeholder="Mot de passe"
                        className="eb-input"
                        inputStyle={newPassword && !isPwdValid ? { borderColor: 'var(--eb-danger)' } : undefined}
                        autoComplete="new-password"
                        pattern={PASSWORD_REGEX.source}
                        title={PASSWORD_POLICY.message}
                        aria-label="Mot de passe"
                        aria-invalid={!!newPassword && !isPwdValid}
                      />
                    </div>

                    <input
                      type="text"
                      className="eb-input"
                      value={newPseudo}
                      onChange={(e) => setNewPseudo(e.target.value)}
                      placeholder="Pseudo"
                      aria-label="Pseudo"
                    />

                    <button
                      type="button"
                      onClick={handleCreateAccount}
                      className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
                      disabled={isCreateDisabled}
                    >
                      Créer
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowLoginForm(true)}
                      className="eb-btn eb-btn--ghost eb-btn--block"
                    >
                      J'ai déjà un compte
                    </button>
                  </div>
                )}

                {showLoginForm && (
                  <div className="csq-modal__body">
                    <div className="csq-modal__head">
                      <button
                        type="button"
                        onClick={() => { setShowLoginForm(false); setLoginHint(null); }}
                        className="csq-modal__back"
                        aria-label="Retour"
                      >
                        ←
                      </button>
                      <h2 className="csq-modal__title">Se connecter</h2>
                    </div>

                    {loginHint && (
                      <div className="csq-hint" role="status">
                        {loginHint}
                      </div>
                    )}

                    <input
                      type="email"
                      className="eb-input"
                      value={loginEmail}
                      onChange={(e) => setLoginEmail(e.target.value)}
                      placeholder="Email"
                      autoComplete="email"
                      inputMode="email"
                      aria-label="Email"
                    />
                    <input
                      type="password"
                      className="eb-input"
                      value={loginPassword}
                      onChange={(e) => setLoginPassword(e.target.value)}
                      placeholder="Mot de passe"
                      autoComplete="current-password"
                      aria-label="Mot de passe"
                    />

                    <button
                      type="button"
                      onClick={handleLoginExisting}
                      className="eb-btn eb-btn--primary eb-btn--lg eb-btn--block"
                      disabled={isLoginLoading || !loginEmail.trim() || !loginPassword.trim()}
                    >
                      {isLoginLoading ? <span className="eb-spinner eb-spinner--sm eb-spinner--light" aria-hidden="true" /> : null}
                      {isLoginLoading ? 'Connexion...' : 'Se connecter'}
                    </button>
                    <button
                      type="button"
                      onClick={handleForgotPassword}
                      className="eb-btn eb-btn--ghost eb-btn--block"
                    >
                      J'ai oublié mon mot de passe
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Animation erreur */}
      {validationFailure && (
        <div className="success-overlay" role="alert">
          <div className="success-card">
            <div className="csq-fail-icon">
              <FaTimesCircle />
            </div>
            <h4 className="csq-feedback-title">Une erreur est survenue</h4>
          </div>
        </div>
      )}

      {showSuccessAnimation && (
        <div className="success-overlay" role="status">
          <div className="success-card">
            <div className="success-check">
              <FaCheckCircle />
            </div>
            <h4 className="csq-feedback-title">Fidélité validée</h4>
          </div>
        </div>
      )}

      {/* Contenu principal */}
      <main className="csq-main">
        <header className="csq-merchant">
          {merchantPhotoURL ? (
            <img
              src={merchantPhotoURL}
              alt="Profil du commerçant"
              className="csq-merchant__avatar"
            />
          ) : (
            <div className="csq-merchant__avatar csq-merchant__avatar--empty" aria-hidden="true">
              <FaUser />
            </div>
          )}
          {merchantPseudo && <h1 className="csq-merchant__name">{merchantPseudo}</h1>}
        </header>

        <section className="csq-card">
          <div className="csq-card__head">
            <h2 className="csq-card__title">Montrez ce code en caisse</h2>
            <p className="csq-card__subtitle">La page se fermera automatiquement</p>
          </div>

          {generatedCode ? (
            <>
              <div className="csq-code">
                <span className="csq-code__value" aria-label={`Code ${generatedCode}`}>{generatedCode}</span>
                <div className="csq-code__barcode">
                  <Barcode
                    value={generatedCode}
                    height={44}
                    width={1.8}
                    displayValue={false}
                    background="#ffffff"
                    lineColor="#101828"
                    margin={0}
                    svgStyle={{ width: '100%' }}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleClose}
                className="eb-btn eb-btn--secondary eb-btn--lg eb-btn--block"
              >
                Fermer
              </button>
            </>
          ) : (
            qrLoading && (
              <div className="csq-code csq-code--loading" aria-busy="true">
                <span className="eb-spinner" aria-hidden="true" />
              </div>
            )
          )}
        </section>
      </main>
    </div>
  );
};

const CSQ_STYLES = `
.csq-page {
  position: relative;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 32px 16px 48px;
  display: flex;
  justify-content: center;
  background:
    radial-gradient(900px 480px at 50% -12%, rgba(39, 76, 116, 0.12), transparent 62%),
    var(--eb-bg);
}
.csq-main {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  animation: eb-fade-up 0.4s var(--eb-ease) both;
}
.csq-merchant {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  text-align: center;
}
.csq-merchant__avatar {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  object-fit: cover;
  background: var(--eb-surface);
  border: 4px solid var(--eb-surface);
  box-shadow: var(--eb-shadow-md);
}
.csq-merchant__avatar--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--eb-brand-50);
  color: var(--eb-brand-300);
  font-size: 38px;
}
.csq-merchant__name {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--eb-text);
  word-break: break-word;
}
.csq-card {
  width: 100%;
  background: var(--eb-surface);
  border: 1px solid var(--eb-border);
  border-radius: var(--eb-radius-xl);
  box-shadow: var(--eb-shadow-md);
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.csq-card__head { text-align: center; }
.csq-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: var(--eb-text);
}
.csq-card__subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--eb-muted);
}
.csq-code {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 20px 18px 18px;
  border: 1.5px dashed var(--eb-border-strong);
  border-radius: var(--eb-radius-lg);
  background: var(--eb-surface-2);
}
.csq-code--loading { min-height: 150px; justify-content: center; }
.csq-code__value {
  font-family: var(--eb-font-mono);
  font-size: 40px;
  font-weight: 700;
  letter-spacing: 0.22em;
  margin-right: -0.22em;
  line-height: 1.1;
  color: var(--eb-text);
  font-variant-numeric: tabular-nums;
}
.csq-code__barcode {
  width: 100%;
  padding: 10px 12px;
  background: #fff;
  border-radius: var(--eb-radius-sm);
  border: 1px solid var(--eb-border);
}
.csq-code__barcode svg { display: block; }

/* Modale */
.csq-overlay {
  position: fixed;
  inset: 0;
  z-index: 10002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(15, 35, 56, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  overflow-y: auto;
}
.csq-modal {
  position: relative;
  width: 100%;
  max-width: 420px;
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: var(--eb-surface);
  border-radius: var(--eb-radius-xl);
  box-shadow: var(--eb-shadow-lg);
  padding: 24px;
}
.csq-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--eb-radius-sm);
  background: transparent;
  color: var(--eb-muted);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
}
.csq-modal__close:hover { background: var(--eb-surface-2); color: var(--eb-text); }
.csq-steps {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 2px 52px 22px 0;
}
.csq-step {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13.5px;
  font-weight: 700;
  background: var(--eb-surface-2);
  border: 1px solid var(--eb-border);
  color: var(--eb-muted);
  transition: background-color 0.2s ease, color 0.2s ease;
}
.csq-step.is-active { background: var(--eb-brand); border-color: var(--eb-brand); color: #fff; box-shadow: var(--eb-ring); }
.csq-step.is-done { background: var(--eb-success); border-color: var(--eb-success); color: #fff; }
.csq-steps__track {
  flex: 1;
  height: 3px;
  border-radius: 3px;
  background: var(--eb-border);
  overflow: hidden;
}
.csq-steps__fill {
  height: 100%;
  background: var(--eb-brand);
  border-radius: 3px;
  transition: width 0.25s var(--eb-ease);
}
.csq-modal__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.csq-modal__head {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
}
.csq-modal__title {
  margin: 0 0 4px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--eb-text);
}
.csq-modal__head .csq-modal__title { margin: 0; }
.csq-modal__back {
  position: absolute;
  left: -6px;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--eb-radius-sm);
  background: transparent;
  color: var(--eb-brand);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}
.csq-modal__back:hover { background: var(--eb-brand-50); }
.csq-hint {
  padding: 10px 12px;
  border-radius: var(--eb-radius-sm);
  font-size: 13.5px;
  line-height: 1.45;
  background: var(--eb-warning-bg);
  border: 1px solid #fde3b0;
  color: var(--eb-warning);
}
.csq-pwd > div { margin-bottom: 0 !important; }
.csq-pwd > ul { margin: 10px 0 0 !important; }
.csq-pwd .eb-input { padding-right: 44px; }
.csq-modal .eb-btn--ghost { min-height: 40px; }

.csq-feedback-title { margin: 0; font-size: 17px; font-weight: 700; color: var(--eb-text); }
.csq-fail-icon {
  width: 84px;
  height: 84px;
  border-radius: 50%;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  color: #fff;
  background: var(--eb-danger);
  animation: successPop 0.35s ease forwards;
}

@media (max-width: 480px) {
  .csq-page { padding: 24px 16px 40px; }
  .csq-overlay { align-items: flex-end; padding: 0; }
  .csq-modal {
    max-width: none;
    max-height: calc(100dvh - 24px);
    border-radius: var(--eb-radius-xl) var(--eb-radius-xl) 0 0;
    padding: 22px 20px calc(20px + env(safe-area-inset-bottom, 0px));
  }
  .csq-code__value { font-size: 36px; }
}
`;

export default ClientScanQRC;
